import axios from 'axios'

export const PROJECT_READY = 'READY'
export const NO_PROJECT_AFFILIATION = 'NO_AFFILIATION'
export const NO_ACTIVE_PROJECT = 'NO_ACTIVE_PROJECT'
export const PROJECT_NOT_CONFIGURED = 'PROJECT_NOT_CONFIGURED'

const VALID_REASON_CODES = new Set([
  PROJECT_READY,
  NO_PROJECT_AFFILIATION,
  NO_ACTIVE_PROJECT
])

const pendingRequests = new Map()

function hasOwn (source, key) {
  return Boolean(source) && Object.prototype.hasOwnProperty.call(source, key)
}

function valueOf (source, ...keys) {
  for (const key of keys) {
    if (hasOwn(source, key)) return source[key]
  }
  return null
}

function contractError (detail) {
  const error = new Error(
    'プロジェクト設定状況を確認できませんでした。' +
    'バックエンドとフロントエンドを最新masterへそろえて再ログインしてください。' +
    (detail ? `（${detail}）` : '')
  )
  error.code = 'PROJECT_PREREQUISITE_CONTRACT_MISMATCH'
  return error
}

function booleanValue (value) {
  if (value === true || value === 1 || value === '1' || value === 'true') return true
  if (value === false || value === 0 || value === '0' || value === 'false') return false
  throw contractError('設定済み区分が不正です')
}

function normalizeProjects (value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  return value.reduce((result, row) => {
    const id = Number(valueOf(row, 'id', 'projectId', 'project_id'))
    const name = String(valueOf(row, 'name', 'projectName', 'project_name') || '').trim()
    if (!Number.isInteger(id) || id <= 0 || !name || seen.has(id)) return result
    seen.add(id)
    result.push({ id, name })
    return result
  }, [])
}

export function normalizeProjectPrerequisite (data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw contractError('応答形式が不正です')
  }

  const projectConfigured = booleanValue(
    valueOf(data, 'projectConfigured', 'project_configured')
  )
  const reasonCode = String(
    valueOf(data, 'reasonCode', 'reason_code') || ''
  ).trim()
  const projects = normalizeProjects(valueOf(data, 'projects'))

  if (!VALID_REASON_CODES.has(reasonCode)) {
    throw contractError('理由コードが不正です')
  }

  if (projectConfigured) {
    const defaultProjectId = Number(
      valueOf(data, 'defaultProjectId', 'default_project_id')
    )
    if (reasonCode !== PROJECT_READY) {
      throw contractError('設定済み状態の理由コードが一致しません')
    }
    if (!Number.isInteger(defaultProjectId) || defaultProjectId <= 0) {
      throw contractError('既定プロジェクトが不足しています')
    }
    if (!projects.some(project => project.id === defaultProjectId)) {
      throw contractError('既定プロジェクトが一覧に存在しません')
    }
    return {
      projectConfigured: true,
      defaultProjectId,
      reasonCode,
      projects
    }
  }

  const defaultProjectValue = valueOf(data, 'defaultProjectId', 'default_project_id')
  if (reasonCode === PROJECT_READY || projects.length ||
      (defaultProjectValue !== null && defaultProjectValue !== undefined &&
       String(defaultProjectValue).trim() !== '')) {
    throw contractError('未設定状態の応答内容が矛盾しています')
  }

  return {
    projectConfigured: false,
    defaultProjectId: null,
    reasonCode,
    projects: []
  }
}

function requestKey (targetUserId) {
  return targetUserId == null ? 'self' : `target:${targetUserId}`
}

export function clearProjectPrerequisiteRequests () {
  pendingRequests.clear()
}

export function getProjectPrerequisite (options = {}) {
  const targetSpecified = options.targetUserId !== null &&
    options.targetUserId !== undefined &&
    String(options.targetUserId).trim() !== ''
  const targetUserId = targetSpecified ? Number(options.targetUserId) : null
  if (targetSpecified && (!Number.isInteger(targetUserId) || targetUserId <= 0)) {
    return Promise.reject(contractError('対象スタッフIDが不正です'))
  }

  const key = requestKey(targetUserId)
  if (pendingRequests.has(key)) return pendingRequests.get(key)

  const path = targetUserId == null
    ? '/project-prerequisite'
    : '/project-prerequisite/target'
  const config = targetUserId == null
    ? undefined
    : { params: { user_id: targetUserId } }

  const request = axios
    .get(process.env.VUE_APP_API_ORIGIN + path, config)
    .then(response => normalizeProjectPrerequisite(response.data))
    .finally(() => pendingRequests.delete(key))

  pendingRequests.set(key, request)
  return request
}

export function projectPrerequisiteMessage (reasonCode, targetMode = false) {
  if (targetMode) {
    if (reasonCode === NO_ACTIVE_PROJECT) {
      return '対象スタッフの所属プロジェクトが削除済み、無効、または現在の契約企業では利用できません。対象スタッフ本人にログインして所属先を選び直してもらってください。'
    }
    return '対象スタッフの所属プロジェクトが設定されていません。対象スタッフ本人にログインして所属先を選択してもらってください。'
  }

  if (reasonCode === NO_ACTIVE_PROJECT) {
    return '所属プロジェクトが削除済み、無効、または現在の契約企業では利用できません。プロジェクト画面で自分の所属先を選び直してください。'
  }
  return '所属プロジェクトが設定されていません。プロジェクト画面で自分の所属先を選択してください。'
}

function accessValues (context) {
  return {
    roles: context && Array.isArray(context.roles) ? context.roles : [],
    features: context && Array.isArray(context.features) ? context.features : [],
    permissions: context && Array.isArray(context.permissions) ? context.permissions : []
  }
}

/** 本人が自分の所属先を設定する画面へ到達できるか。 */
export function canManageProjectConfiguration (context) {
  const { roles, features, permissions } = accessValues(context)
  if (!features.includes('PROJECT')) return false
  return roles.includes('TENANT_OWNER') ||
    permissions.includes('PROJECT_EDIT') ||
    permissions.includes('PROJECT_MANAGE')
}

/** 管理者が対象社員を明示して所属を代理設定できるか。 */
export function canManageOtherUserProjectMembership (context) {
  const { features, permissions } = accessValues(context)
  return features.includes('PROJECT') && permissions.includes('PROJECT_MANAGE')
}

export function safeProjectReturnPath (value) {
  const path = String(value || '').trim()
  if (!path.startsWith('/') || path.startsWith('//')) return '/dashboard'
  if (path.startsWith('/project-prerequisite-required')) return '/dashboard'
  return path
}

export function prerequisiteFromError (error) {
  const response = error && error.response
  const status = response ? Number(response.status) : 0
  const data = response && response.data
  if (status !== 409 || !data || String(data.code || '') !== PROJECT_NOT_CONFIGURED) return null
  const reasonCode = String(data.reasonCode || data.reason_code || '').trim()
  if (!VALID_REASON_CODES.has(reasonCode) || reasonCode === PROJECT_READY) return null
  return {
    projectConfigured: false,
    defaultProjectId: null,
    reasonCode,
    projects: []
  }
}
